import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavigation } from "@/contexts/NavigationContext";
import type { BreadcrumbItem as BreadcrumbItemType } from "@/types/navigation.types";

interface BreadcrumbProps {
  items: BreadcrumbItemType[];
  className?: string;
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({ items, className }) => {
  return (
    <nav aria-label="Breadcrumb" className={cn("flex items-center", className)}>
      <BreadcrumbList>
        {items.map((item, index) => (
          <React.Fragment key={index}>
            {index > 0 && <BreadcrumbSeparator />}
            <BreadcrumbItem>
              {item.isCurrentPage ? (
                <BreadcrumbPage>{item.label}</BreadcrumbPage>
              ) : item.href ? (
                <BreadcrumbLink href={item.href}>{item.label}</BreadcrumbLink>
              ) : (
                <span className="text-muted-foreground">{item.label}</span>
              )}
            </BreadcrumbItem>
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </nav>
  );
};

export const BreadcrumbList: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <ol className="flex items-center gap-2 text-sm" role="list">
      {children}
    </ol>
  );
};

export const BreadcrumbItem: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <li className="flex items-center gap-2" role="listitem">
      {children}
    </li>
  );
};

interface BreadcrumbLinkProps {
  href: string;
  children: React.ReactNode;
}

export const BreadcrumbLink: React.FC<BreadcrumbLinkProps> = ({
  href,
  children,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    triggerWorkflowExit,
    triggerTimelineExit,
    checkWorkflowNavigation,
    checkTimelineNavigation,
  } = useNavigation();
  const isOnWorkflowPage = location.pathname.includes("/workflows/");
  const isOnTimelinePage = location.pathname.includes("/video-editor/");

  const handleClick = async (e: React.MouseEvent) => {
    // If navigating away from a workflow page, check for unsaved changes first
    if (isOnWorkflowPage) {
      e.preventDefault();
      // Check for unsaved changes - this may show a dialog
      const canNavigate = await checkWorkflowNavigation(href);
      if (!canNavigate) {
        return; // User cancelled, stay on page
      }
      // User confirmed or no unsaved changes, trigger exit animation
      await triggerWorkflowExit();
      navigate(href, { state: { fromWorkflow: true } });
    }
    // If navigating away from a timeline page, check for unsaved changes first
    else if (isOnTimelinePage) {
      e.preventDefault();
      // Check for unsaved changes - this may show a dialog
      const canNavigate = await checkTimelineNavigation(href);
      if (!canNavigate) {
        return; // User cancelled, stay on page
      }
      // User confirmed or no unsaved changes, trigger exit animation
      await triggerTimelineExit();
      navigate(href, { state: { fromTimeline: true } });
    }
  };

  return (
    <Link
      to={href}
      state={{ fromWorkflow: isOnWorkflowPage, fromTimeline: isOnTimelinePage }}
      onClick={handleClick}
      className="text-muted-foreground hover:text-foreground transition-colors duration-200 hover:underline"
    >
      {children}
    </Link>
  );
};

export const BreadcrumbPage: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <span className="font-medium text-foreground" aria-current="page">
      {children}
    </span>
  );
};

export const BreadcrumbSeparator: React.FC = () => {
  return (
    <ChevronRight
      className="h-4 w-4 text-muted-foreground"
      aria-hidden="true"
    />
  );
};
